import React, { useState } from 'react';
import { GlassCard, Button, Icon } from '../../campaign-os';
import { CHECKS, formatInr, toneFor } from '../data/selfRegularization';

/**
 * The two pieces that make the regularization screens work for the admin instead of the
 * other way round: a picture of the whole base (who is verified, who is not yet, who cannot
 * be until they supply something) and suggestions that select the right vendors in one click.
 */

const num = (n) => Number(n || 0).toLocaleString('en-IN');
const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);

/** One check's bar: verified · not yet · missing, with the counts under it. */
function HealthBar({ check, counts, total, onPick }) {
  const v = pct(counts.verified, total);
  const u = pct(counts.unverified, total);
  const m = Math.max(0, 100 - v - u);
  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[12px] font-semibold text-cos-ink">
          <Icon name={check.icon} className={`text-[16px] ${toneFor(check.key).text}`} />
          {check.label}
        </span>
        <span className="mono text-[12px] text-cos-ink">{v}%</span>
      </div>
      <div className="mt-1.5 flex h-2 w-full overflow-hidden rounded-full bg-[rgba(70,80,72,0.08)]" role="img" aria-label={`${check.label}: ${counts.verified} verified, ${counts.unverified} not yet, ${counts.missing} missing`}>
        <span className={`h-full transition-[width] duration-500 ${toneFor(check.key).bar}`} style={{ width: `${v}%` }} />
        <span className="h-full bg-cos-beige-dark/70 transition-[width] duration-500" style={{ width: `${u}%` }} />
        <span className="h-full bg-cos-status-coral/50 transition-[width] duration-500" style={{ width: `${m}%` }} />
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-2xs">
        <span className="text-cos-ink-secondary"><span className="mono font-semibold text-cos-teal-deep">{num(counts.verified)}</span> verified</span>
        <button type="button" onClick={() => onPick?.('unverified', check.key)} className="text-cos-ink-secondary hover:text-cos-ink hover:underline" title={`Show the ${num(counts.unverified)} with a ${check.needsLabel} that was never confirmed`}>
          <span className={`mono font-semibold ${toneFor(check.key).text}`}>{num(counts.unverified)}</span> not yet
        </button>
        <button type="button" onClick={() => onPick?.('missing', check.key)} className="text-cos-ink-secondary hover:text-cos-ink hover:underline" title={`Show the ${num(counts.missing)} with no ${check.needsLabel}`}>
          <span className="mono font-semibold text-cos-status-coral">{num(counts.missing)}</span> missing
        </button>
      </div>
    </div>
  );
}

/**
 * The base at a glance. `health` is baseHealth(); `onPick(kind, checkKey)` lets a number be a
 * filter — click "missing" under GST and the list below shows exactly those vendors.
 */
export function BaseHealthStrip({ health, loading, onPick, footer }) {
  if (loading || !health) {
    return (
      <GlassCard>
        <div className="flex items-center gap-2 text-[13px] text-cos-ink-secondary">
          <Icon name="hourglass_top" className="text-[18px] text-cos-teal-deep" /> Reading the vendor base…
        </div>
      </GlassCard>
    );
  }
  const fully = pct(health.fully_verified, health.total);
  return (
    <GlassCard>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-8">
        <div className="shrink-0 lg:w-[190px]">
          <div className="eyebrow">The vendor base</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="mono text-[34px] font-bold leading-none tracking-tight text-cos-ink">{num(health.total)}</span>
            <span className="text-[12px] text-cos-ink-secondary">vendors</span>
          </div>
          <div className="mt-2 flex flex-col gap-1 text-[12px] text-cos-ink-secondary">
            <span><span className="mono font-semibold text-cos-teal-deep">{fully}%</span> fully verified</span>
            <span><span className="mono font-semibold text-cos-status-coral">{num(health.nothing_verified)}</span> with nothing verified</span>
          </div>
        </div>
        <div className="grid min-w-0 flex-1 grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-4">
          {CHECKS.map((c) => (
            <HealthBar key={c.key} check={c} counts={health.checks[c.key]} total={health.total} onPick={onPick} />
          ))}
        </div>
      </div>
      {footer && <div className="mt-4 border-t border-cos-line-subtle pt-3">{footer}</div>}
    </GlassCard>
  );
}

/** One suggestion, with the one click that acts on it. */
/** The check a suggestion is about, so it can borrow that check's colour. */
const suggestionCheck = (s) => s.checks?.[0] ?? s.asks?.[0] ?? (s.key?.startsWith('missing:') ? s.key.slice(8) : null);

export function SuggestionCard({ suggestion, actionLabel, onAct, onPreview, disabled, busy }) {
  const n = suggestion.vendorIds.length;
  const nudge = suggestion.kind === 'remind' || suggestion.kind === 'resend';
  const tone = toneFor(suggestionCheck(suggestion));
  const iconClass = nudge ? 'bg-cos-status-amber-bg text-cos-status-amber' : tone.icon;
  return (
    <div className="flex min-w-0 items-center gap-3 rounded-xl border border-cos-line bg-white/60 px-3 py-2.5 transition-colors hover:border-cos-teal/40 hover:bg-white/85">
      <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${iconClass}`}>
        <Icon name={suggestion.icon} className="text-[18px]" weight={500} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-1.5">
          <span className="mono text-[15px] font-bold leading-none text-cos-ink">{num(n)}</span>
          <span className="truncate text-[13px] font-semibold text-cos-ink">{suggestion.title}</span>
          {suggestion.cost_inr !== undefined && (
            <span className="mono text-[12px] text-cos-ink-muted">· {suggestion.cost_inr === 0 ? 'free' : formatInr(suggestion.cost_inr)}</span>
          )}
        </div>
        <p className="truncate text-[11px] leading-[16px] text-cos-ink-secondary" title={suggestion.detail}>{suggestion.detail}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {onPreview && (
          <button
            type="button"
            onClick={() => onPreview(suggestion)}
            className="grid h-8 w-8 place-items-center rounded-lg text-cos-ink-muted transition-colors hover:bg-white hover:text-cos-ink"
            title="Show me these vendors"
          >
            <Icon name="visibility" className="text-[17px]" />
          </button>
        )}
        <Button variant="primary" size="sm" icon={busy ? 'hourglass_top' : suggestion.kind === 'run' ? 'play_arrow' : nudge ? 'notifications_active' : 'send'} onClick={() => onAct(suggestion)} disabled={disabled || busy}>
          {busy ? 'Working…' : actionLabel}
        </Button>
      </div>
    </div>
  );
}

/** The row of suggestions. Nothing to suggest is a good state, and says so. */
const SHOWN = 3;

export function SuggestionRow({ title, subtitle, suggestions, actionLabelFor, onAct, onPreview, disabled, busyKey }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? suggestions : suggestions.slice(0, SHOWN);
  const hidden = suggestions.length - visible.length;
  return (
    <GlassCard accentRail>
      <div className="mb-2.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="text-[14px] font-bold tracking-tight text-cos-ink">{title}</h3>
        <p className="min-w-0 flex-1 truncate text-[12px] text-cos-ink-secondary" title={subtitle}>{subtitle}</p>
      </div>
      {suggestions.length === 0 ? (
        <p className="flex items-center gap-2 text-[13px] text-cos-ink-secondary">
          <Icon name="task_alt" className="text-[18px] text-cos-teal-deep" /> Nothing waiting. Every record is either verified or already asked.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
            {visible.map((s) => (
              <SuggestionCard key={s.key} suggestion={s} actionLabel={actionLabelFor(s)} onAct={onAct} onPreview={onPreview} disabled={disabled} busy={busyKey === s.key} />
            ))}
          </div>
          {(hidden > 0 || expanded) && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-cos-teal-deep hover:underline"
            >
              <Icon name={expanded ? 'expand_less' : 'expand_more'} className="text-[16px]" />
              {expanded ? 'Show fewer' : `${hidden} more`}
            </button>
          )}
        </>
      )}
    </GlassCard>
  );
}
